/** Justified rows (Google-Photos style), the same math as the prototype's `layoutDay`. */

export interface JustifyItem {
  /** width / height */
  ar: number;
}

export interface JustifiedTile<T> {
  item: T;
  width: number;
  height: number;
}

export interface JustifiedRow<T> {
  tiles: JustifiedTile<T>[];
  height: number;
}

/** Target row height for a viewport width, as in the prototype. */
export function targetRowHeight(viewportWidth: number): number {
  return viewportWidth < 640 ? 130 : viewportWidth < 1100 ? 200 : 240;
}

/**
 * Packs items into rows that exactly fill `width`. A row is closed once its height at the target size would overflow;
 * its height is then scaled so that the row (tiles + gaps) fills the width. The last row is capped at the target
 * height when stretching it would make it more than 25% taller than the target.
 */
export function justify<T extends JustifyItem>(
  items: readonly T[],
  width: number,
  target: number,
  gap = 4,
): JustifiedRow<T>[] {
  const W = Math.max(1, width);
  const rows: JustifiedRow<T>[] = [];
  let row: { item: T; ar: number }[] = [];
  let sum = 0;

  const flush = (last: boolean) => {
    if (!row.length) return;
    let h = (W - gap * (row.length - 1)) / sum;
    if (last && h > target * 1.25) h = target;
    rows.push({
      height: Math.round(h),
      tiles: row.map(({ item, ar }) => ({ item, width: Math.floor(ar * h), height: Math.round(h) })),
    });
    row = [];
    sum = 0;
  };

  for (const it of items) {
    const ar = it.ar > 0 && Number.isFinite(it.ar) ? it.ar : 1;
    row.push({ item: it, ar });
    sum += ar;
    if (sum * target + gap * (row.length - 1) >= W) flush(false);
  }
  flush(true);
  return rows;
}
